import {MedicalReportType, PatientAgeRange, UploadedDocument} from '@/types'
import {ProjectSmall} from '@/features/projects/types'
import {TeamGroup} from '@/features/treatment-requests/teams/types'
import i18n from '@/translations/i18n'
import {View} from 'react-big-calendar'
import {z} from 'zod'
import {AccessFrequency} from '@/features/planned-accesses/types'
import {AccessGroupNameEnum, SiarEventTypeEnum} from '@/features/access-types/types'

export const ConsultantAbsenceReason = z.enum(['illness', 'personal_reasons', 'training', 'leave'])
export type ConsultantAbsenceReason = z.infer<typeof ConsultantAbsenceReason>

export type SearchTarget = 'patients' | 'users' | 'team'

export const AgendaSearchParams = z.object({
    category: z
        .enum(['team', 'adults', 'evolutionary_age_adults', 'evolutionary_age', 'referring_doctor', 'custom'])
        .optional(),
    patientId: z.string().optional(),
    specializationIds: z.string().array().optional(),
    subjectIds: z.string().array().optional(),
    hideDomiciliary: z.literal('true').optional(),
    hideHolidays: z.literal('true').optional()
})
export type AgendaSearchParams = z.infer<typeof AgendaSearchParams>

export const EventTypeKeyName = z.enum([
    'cardiac_holter',
    'cardiology_visit',
    'certification',
    'cognitive_neuropsych_rehab',
    'counseling',
    'cycle_closing_processing',
    'cycle_opening_processing',
    'educational_intervention',
    'educator_evaluation',
    'orl_visit',
    'extension_processing',
    'glo_meeting',
    'initial_project_evaluation',
    'initial_social_service_eval',
    'intermediate_project_evaluation',
    'neurological_specialist_visit',
    'neuromotor_evaluation',
    'neuromotor_rehab',
    'neuropsychiatric_specialist_visit',
    'nutritional_visit',
    'occupational_therapy',
    'occupational_therapy_evaluation',
    'orthopedic_visit',
    'parent_training',
    'pediatric_visit',
    'physiatric_specialist_visit',
    'project_closing_processing',
    'project_opening_processing',
    'project_renewal_processing',
    'psychological_evaluation',
    'psychological_therapy',
    'psychomotor_evaluation',
    'psychomotor_therapy',
    'pulmonology_visit',
    'short_extension_processing',
    'short_team_meeting',
    'social_service_evaluation',
    'speech_evaluation',
    'speech_therapy',
    'team_meeting',
    'update_medical_record',
    'medical_interview',
    'therapy_evaluation',
    // SIR
    'project_management_and_revision_sir',
    'team_meeting_sir',
    'observation_and_evaluation_sir'
])
export type EventTypeKeyName = z.infer<typeof EventTypeKeyName>

const AgendaEntity = z.enum(['users', 'patients'])
export type AgendaEntity = z.infer<typeof AgendaEntity>

export interface AgendaDateRange {
    startDate: string
    endDate: string
}

export const AgendaUrlParams = z.object({
    entity: AgendaEntity.optional(),
    view: z.enum(['month', 'week', 'day', 'agenda']).optional(),
    date: z.string().min(1).optional()
})
export type AgendaUrlParams = z.infer<typeof AgendaUrlParams>

export const AgendaEventStatus = z.enum([
    'provisional',
    'scheduled',
    'completed',
    'missed',
    'cancelled',
    'withdrawn',
    'to_be_confirmed',
    'tentative',
    'to_reschedule'
])
export type AgendaEventStatus = z.infer<typeof AgendaEventStatus>

export const EventType = z.enum(['event', 'patient_absence', 'consultant_absence', 'generic_event'])
export type EventType = z.infer<typeof EventType>

export const EventCategory = z.enum([
    'project_management',
    'team_meeting',
    'specialist_visits',
    'treatments',
    'evaluations'
])
export type EventCategory = z.infer<typeof EventCategory>

export const BlockoutDetails = z.object({
    type: z.literal('blockout'),
    name: z.string()
})

export const AppointmentDetails = z.object({
    type: z.literal('appointment'),
    fullName: z.string(),
    activity: z.string(),
    status: AgendaEventStatus
})
export type AgendaView = Extract<View, 'day' | 'week' | 'month' | 'agenda'>

export const EventRoom = z.enum(['clinic', 'gym', 'domiciliary'])
export type EventRoom = z.infer<typeof EventRoom>

export const EventTeamMember = z.object({
    id: z.string().min(1),
    treatmentRequestTeamId: z.string().min(1),
    member: z.object({
        group: TeamGroup,
        userId: z.string().min(1),
        fullName: z.string().min(1),
        specializationName: z.string(),
        specializationId: z.string().optional(),
        isAReserve: z.boolean()
    })
})
export type EventTeamMember = z.infer<typeof EventTeamMember>

export const EventSmall = z.object({
    id: z.string(),
    type: EventType,
    date: z.string(),
    time: z.string().nullable(),
    duration: z.number().nullable(),
    category: EventCategory.nullable(),
    attachment: UploadedDocument.nullish(),
    isGroupTreatment: z.boolean().nullable(),
    status: AgendaEventStatus.nullable(),
    location: EventRoom.nullable(),
    note: z.string().nullable(),
    isRemote: z.coerce.boolean(),
    isARecovery: z.coerce.boolean(),
    missedAccessId: z.string().nullable(),
    communicatedOnTime: z.coerce.boolean().nullable(),
    attachmentId: z.string().nullable(),
    sector: z.enum(['outpatient', 'domiciliary', 'mixed']),
    remoteUrl: z.string().nullable(),
    frequency: z.enum(['weekly', 'monthly']).nullable(),
    position: z.number().min(1).max(5).nullable(),
    dayOfWeek: z.enum(['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']).nullable(),
    eventPatients: z
        .array(
            z.object({
                patientId: z.string(),
                projectId: z.string().nullable(),
                patient: z
                    .object({
                        fullName: z.string()
                    })
                    .nullable(),
                medicalRecord: z
                    .object({
                        id: z.string()
                    })
                    .nullable(),
                project: ProjectSmall.pick({
                    id: true,
                    treatmentRequestId: true,
                    code: true,
                    startDate: true,
                    status: true,
                    expirationDate: true,
                    type: true,
                    isSuspended: true
                }).nullable()
            })
        )
        .nullable(),
    eventConsultants: z
        .array(
            z.object({
                userId: z.string(),
                accessTypeId: z.string().nullable(),
                user: z.object({
                    specialization: z.string(),
                    specializationId: z.string(),
                    fullName: z.string()
                }),
                substituteId: z.string().nullable(),
                substitute: z
                    .object({
                        id: z.string(),
                        fullName: z.string()
                    })
                    .nullable(),
                accessType: z
                    .object({
                        id: z.string(),
                        accessType: EventTypeKeyName,
                        medicalRecordReportType: MedicalReportType.nullable(),
                        siarAccessType: SiarEventTypeEnum.nullable(),
                        groupName: AccessGroupNameEnum.nullable()
                    })
                    .nullable()
            })
        )
        .nullable(),
    eventTeams: EventTeamMember.array(),
    absenceMotivation: ConsultantAbsenceReason.nullable()
})
export type EventSmall = z.infer<typeof EventSmall>

export const EventExtended = EventSmall.extend({
    eventPatients: z
        .array(
            z.object({
                patientId: z.string(),
                projectId: z.string().nullable(),
                patient: z.object({
                    fullName: z.string(),
                    neuromuscularDiagnosisId: z.string().nullable()
                }),
                medicalRecord: z
                    .object({
                        id: z.string()
                    })
                    .nullable(),
                project: ProjectSmall.pick({
                    id: true,
                    treatmentRequestId: true,
                    code: true,
                    startDate: true,
                    status: true,
                    expirationDate: true,
                    type: true,
                    isSuspended: true,
                    sector: true
                }).nullable()
            })
        )
        .nullable(),
    communicatedOnTime: z.boolean().nullable(),
    attachmentId: z.string().nullable(),
    missedAccess: z
        .object({
            id: z.string(),
            projectId: z.string(),
            remappedSiarAccessType: z
                .enum([
                    'Valutazione e osservazione',
                    'Riabilitazione neuromotoria',
                    'Riabilitazione logopedica',
                    'Riabilitazione psicomotoria',
                    'Riabilitazione cognitiva-neuropsicologica',
                    'Parent training',
                    'Terapia occupazionale',
                    'Intervento educativo',
                    'Elaborazione',
                    '<15min (non contabilizzato)',
                    'Counseling',
                    'Adempimenti Legge 104'
                ])
                .nullable(),
            type: z.enum(['absence', 'access']),
            payOutcome: z.enum(['absence_20', 'absence_50', 'absence_nr', 'access_100']),
            patientId: z.string(),
            consultantId: z.string().nullable(),
            event: EventSmall,
            rateCode: z.enum(['AA1', 'AA2', 'AM', 'D1', 'D2']).nullable(),
            status: z.enum(['validated', 'to_validate', 'excluded']),
            specification: z.string().nullable(),
            sector: ProjectSmall.shape.sector,
            project: ProjectSmall.pick({
                sector: true,
                code: true,
                startDate: true,
                expirationDate: true,
                status: true
            }).nullable(),
            patient: z
                .object({
                    serviceType: PatientAgeRange.exclude(['evolutionary_age_adults']),
                    residenceAslDistrict: z
                        .object({
                            aslName: z.string().nullable(),
                            name: z.string().nullable()
                        })
                        .nullable()
                })
                .nullable(),
            medicalRecordReport: z
                .object({
                    id: z.string()
                })
                .nullable()
        })
        .nullable()
})
export type EventExtended = z.infer<typeof EventExtended>

// Availabilities
export const Availability = z.record(z.string(), z.string().array())
export type Availability = z.infer<typeof Availability>

export const AgendaEventConsultant = z.object({
    userId: z.string(),
    accessTypeId: z.string(),
    user: z.object({
        specialization: z.string(),
        specializationId: z.string(),
        fullName: z.string()
    }),
    accessType: z.object({
        id: z.string(),
        accessType: EventTypeKeyName,
        medicalRecordReportType: MedicalReportType.nullable()
    })
})
export type AgendaEventConsultant = z.infer<typeof AgendaEventConsultant>

export const ConsultantNextAvailability = z.object({
    id: z.string().min(1),
    fullName: z.string().min(1),
    specializationName: z.string().min(1),
    dates: z.array(z.string())
})
export type ConsultantNextAvailability = z.infer<typeof ConsultantNextAvailability>

// Type specific for the agenda component
const EventAppointment = z.discriminatedUnion('type', [
    z
        .object({
            type: z.literal('event'),
            status: AgendaEventStatus,
            eventConsultants: z.array(AgendaEventConsultant).nullable()
        })
        .merge(
            EventSmall.pick({
                id: true,
                date: true,
                time: true,
                duration: true,
                category: true,
                eventTeams: true,
                eventPatients: true,
                isGroupTreatment: true,
                isRemote: true,
                isRecovery: true,
                frequency: true
            })
        )
])

// Type specific for the agenda component
const EventGeneric = z.discriminatedUnion('type', [
    z
        .object({
            type: z.literal('generic_event'),
            status: AgendaEventStatus,
            eventConsultants: z.array(AgendaEventConsultant).nullable()
        })
        .merge(
            EventSmall.pick({
                id: true,
                date: true,
                time: true,
                duration: true,
                note: true
            })
        )
])

const EventAbsence = z.discriminatedUnion('type', [
    z
        .object({
            type: z.enum(['patient_absence', 'consultant_absence'])
        })
        .merge(
            EventSmall.pick({
                id: true,
                date: true,
                time: true,
                duration: true,
                eventConsultants: true,
                eventPatients: true,
                absenceMotivation: true,
                communicatedOnTime: true
            })
        )
])

const EventUnavailability = z.discriminatedUnion('type', [
    z
        .object({
            type: z.literal('unavailability')
        })
        .merge(
            EventSmall.pick({
                id: true
            })
        )
])

// The type of an event (event, absence or unavailability) in the agenda
const AgendaEventType = z
    .object({
        start: z.date(),
        end: z.date(),
        isDraggable: z.boolean().optional(),
        resourceId: z.string().nullable(),
        status: AgendaEventStatus,
        sector: z.enum(['outpatient', 'domiciliary']),
        time: z.string()
    })
    .and(
        z.discriminatedUnion('type', [
            ...EventAppointment.options,
            ...EventAbsence.options,
            ...EventUnavailability.options,
            ...EventGeneric.options
        ])
    )
export type AgendaEventType = z.infer<typeof AgendaEventType>

export type AgendaEventAppointment = Extract<AgendaEventType, {type: 'event'}>
export type AgendaEventGeneric = Extract<AgendaEventType, {type: 'generic_event'}>
export type AgendaEventAbsence = Extract<AgendaEventType, {type: 'patient_absence' | 'consultant_absence'}>
export type AgendaEventUnavailability = Extract<AgendaEventType, {type: 'unavailability'}>

// The type of an team resource
export interface AgendaTeamResource {
    resourceId: string
    resourceTitle: string
    specializationName: string
}

// The type of an equipe member
export const AgendaSubject = z.object({
    id: z.string().min(1),
    fullName: z.string().min(1),
    specializationId: z.string().optional(),
    specializationName: z.string().optional(),
    project: z
        .object({
            id: z.string().min(1),
            code: ProjectSmall.shape.code,
            status: ProjectSmall.shape.status,
            startDate: ProjectSmall.shape.startDate,
            expirationDate: ProjectSmall.shape.expirationDate,
            type: ProjectSmall.shape.type
        })
        .nullish(),
    treatmentRequestId: z.string().nullish(),
    neuromuscularDiagnosisId: z.string().nullish()
})
export type AgendaSubject = z.infer<typeof AgendaSubject>

// The type of an event view
export const AgendaEventView = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    type: z.enum(['private', 'public'])
})
export type AgendaEventView = z.infer<typeof AgendaEventView>

export const AgendaConsultant = z.object({
    id: z.string().min(1, {message: i18n.t('errors:required')}),
    fullName: z.string().min(1),
    group: TeamGroup.optional(),
    specializationName: z.string().optional(),
    specializationId: z.string().optional(),
    substitute: z
        .object({
            id: z.string(),
            fullName: z.string()
        })
        .nullish(),
    accessType: z
        .object({
            id: z.string().min(1),
            accessType: EventTypeKeyName,
            frequency: AccessFrequency.nullish()
        })
        .nullish()
})
export type AgendaConsultant = z.infer<typeof AgendaConsultant>

export const AgendaPatient = z.object({
    id: z.string().min(1, {message: i18n.t('errors:required')}),
    fullName: z.string().min(1, {message: i18n.t('errors:required')}),
    treatmentRequestId: z.string().nullable(),
    neuromuscularDiagnosisId: z.string().nullable(),
    project: ProjectSmall.pick({
        id: true,
        code: true,
        status: true,
        startDate: true,
        expirationDate: true,
        type: true,
        treatmentRequestId: true,
        isSuspended: true
    }).nullable()
})
export type AgendaPatient = z.infer<typeof AgendaPatient>

export type AgendaModalType = 'edit' | 'details' | 'create'

export type RepetitionOptions = 'weekly' | 'monthly' | 'lastDayOfMonth'
export type RepetitionReturn = {
    [key in RepetitionOptions]?: {
        label: string
        value: {
            id: string
            day: string
            localeDay: string
            frequency: Extract<RepetitionOptions, 'weekly' | 'monthly'>
            position: 1 | 2 | 3 | 4 | 5
        }
    }
}
